import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { ShareCostLogo } from '@/components/common/ShareCostLogo';
import { Footer } from '@/components/layout/Footer';
import { Analytics } from '@/services/analytics.service';
import { useTranslation } from 'react-i18next';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await resetPassword(email);
      Analytics.track({ name: 'Password Reset Requested', properties: { email } });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('web:forgotPasswordPage.failedToSendResetEmail'));
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-secondary">
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">{t('auth:signUp.checkEmail.title')}</h2>
            <p className="mt-2 text-text-secondary">
              {t('web:forgotPasswordPage.weSentPasswordResetInstructionsTo')} <strong>{email}</strong>.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('web:common.backToSignIn')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-secondary">
      <div className="flex flex-1 items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <ShareCostLogo className="mx-auto mb-4 h-14 w-14 rounded-xl" />
          <h1 className="text-2xl font-bold text-text-primary">{t('web:forgotPasswordPage.resetYourPassword')}</h1>
          <p className="mt-1 text-text-secondary">{t('web:forgotPasswordPage.enterYourEmailAndWeLl')}</p>
        </div>

        <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary">
                {t('auth:form.placeholder.email')}
              </label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-border-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('web:forgotPasswordPage.sendResetLink')
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('web:common.backToSignIn')}
          </Link>
        </p>
      </div>
      </div>
      <Footer />
    </div>
  );
}
